import React from 'react';
import { SHIFT_TYPE_LABELS, WORK_TYPE_LABELS, DRIVE_TYPE_LABELS, formatPay } from '../utils/formatters';
import { POST_EXPIRY_DAYS } from '../constants/limits';
import './PostListItem.css';

const PostListItem = ({
  post,
  userRole,
  userId,
  onViewDetails,
  onViewProfile,
  onEdit,
  onDelete,
  onRenew
}) => {
  const isOwnerPost = post.post_type === 'owner' || post.post_type === 'shift';
  const isDriverPost = post.post_type === 'driver';
  const isAdmin = userRole === 'admin';

  const isOwnPost = isOwnerPost
    ? post.fleet_id === userId
    : post.driver_id === userId;

  const formattedPay = isOwnerPost
    ? formatPay(post.pay_rate, post.pay_type)
    : null;

  const getShiftType = () => post.shift_type || null;

  const getShiftBadgeClass = (shiftType) => {
    if (!shiftType || typeof shiftType !== 'string') return 'any';
    if (shiftType === 'dov') return 'dov';
    if (shiftType.includes('day')) return 'day';
    if (shiftType.includes('night')) return 'night';
    if (shiftType.includes('24')) return '24hr';
    return 'any';
  };

  const getVehicleTypes = () => {
    if (!post.vehicle_types) return [];
    return post.vehicle_types.split(',').map(v => v.trim());
  };

  const getBorderClass = () => {
    if (isOwnerPost) return `status-${post.status}`;
    return `type-driver status-${post.status}`;
  };

  return (
    <div className={`post-card ${getBorderClass()} ${isOwnPost ? 'my-post' : ''}`}>
      {/* Left Icon Panel */}
      <div className={`post-icon-panel ${isOwnerPost ? 'car-type' : 'driver-type'}`}>
        {isOwnerPost ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
            </svg>
            <span className="icon-label">Looking for Driver</span>
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            <span className="icon-label">Looking for Vehicle</span>
          </>
        )}
      </div>

      {/* Right Content Panel */}
      <div className="post-content">
        {/* Title Row */}
        <div className="post-title-row">
          <div className="post-title-info">
            <h3 className="post-title">{post.title}</h3>
            <div className="post-meta">
              {isOwnerPost ? (
                <>
                  {!isOwnPost && post.fleet_name && <span>by {post.fleet_name}</span>}
                  {formattedPay && <span className="post-rate">{formattedPay}</span>}
                  {post.garage_address && <span>{post.garage_address}</span>}
                </>
              ) : (
                <>
                  {!isOwnPost && post.driver_name && <span>{post.driver_name}</span>}
                  {post.years_experience && <span>{post.years_experience} yrs exp</span>}
                </>
              )}
            </div>
          </div>

          <div className="post-actions">
            {isOwnerPost && onViewDetails && (
              <button className="btn-action btn-primary" onClick={() => onViewDetails(post)}>
                Details
              </button>
            )}
            {isDriverPost && !isOwnPost && onViewProfile && (
              <button className="btn-action btn-primary" onClick={() => onViewProfile(post)}>
                Profile
              </button>
            )}
            {isOwnPost && onRenew && (
              <button className="btn-action btn-renew" onClick={() => onRenew(post.id, post.post_type)}>
                Renew
              </button>
            )}
            {isOwnPost && onEdit && (
              <button className="btn-action btn-secondary" onClick={() => onEdit(post)}>
                Edit
              </button>
            )}
            {isOwnPost && onDelete && (
              <button className="btn-action btn-danger" onClick={() => onDelete(post.id, post.post_type)}>
                Delete
              </button>
            )}
            {isAdmin && !isOwnPost && (
              <>
                {onEdit && (
                  <button className="btn-action btn-secondary" onClick={() => onEdit(post)}>
                    Edit
                  </button>
                )}
                {onDelete && (
                  <button className="btn-action btn-danger" onClick={() => onDelete(post.id, post.post_type)}>
                    Delete
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Description */}
        {post.description && (
          <p className="post-description">{post.description}</p>
        )}

        {/* Tags Row */}
        <div className="post-tags">
          {isOwnerPost ? (
            <>
              <span className={`tag tag-shift ${getShiftBadgeClass(post.shift_type)}`}>
                {SHIFT_TYPE_LABELS[post.shift_type]}
              </span>
              <span className={`tag tag-vehicle vehicle-${post.vehicle_type}`}>
                {WORK_TYPE_LABELS[post.vehicle_type] || post.vehicle_type}
              </span>
              {post.drive_type && post.drive_type !== 'standard' && (
                <span className={`tag tag-drive drive-${post.drive_type}`}>
                  {DRIVE_TYPE_LABELS[post.drive_type]}
                </span>
              )}
              {post.wav ? <span className="tag tag-wav">WAV</span> : null}
              {post.vehicle_make && (
                <span className="tag tag-car">
                  {post.vehicle_year} {post.vehicle_make} {post.vehicle_model || ''}
                </span>
              )}
            </>
          ) : (
            <>
              {getShiftType() ? (
                <span className={`tag tag-shift ${getShiftBadgeClass(getShiftType())}`}>
                  {SHIFT_TYPE_LABELS[getShiftType()] || getShiftType()}
                </span>
              ) : (
                <span className="tag tag-shift any">Any Shift</span>
              )}
              {getVehicleTypes().length > 0 ? (
                getVehicleTypes().map(v => (
                  <span key={v} className={`tag tag-vehicle vehicle-${v}`}>
                    {WORK_TYPE_LABELS[v] || v}
                  </span>
                ))
              ) : (
                <span className="tag tag-vehicle">Any Vehicle</span>
              )}
              {post.drive_type && (
                <span className={`tag tag-drive drive-${post.drive_type}`}>
                  {DRIVE_TYPE_LABELS[post.drive_type]}
                </span>
              )}
              {post.wav ? <span className="tag tag-wav">WAV</span> : null}
            </>
          )}

          {post.days_until_expiry !== undefined && (
            <span className={`tag tag-time ${isOwnPost && post.days_until_expiry <= 3 ? 'expiring' : ''}`}>
              {isOwnPost
                ? (post.days_until_expiry <= 0 ? 'Expiring today' : `${post.days_until_expiry}d left`)
                : (POST_EXPIRY_DAYS - post.days_until_expiry <= 0 ? 'Today' : `${POST_EXPIRY_DAYS - post.days_until_expiry}d ago`)
              }
            </span>
          )}
        </div>

      </div>
    </div>
  );
};

export default PostListItem;
